import { useState, useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import Tooltip from '@mui/material/Tooltip';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { green, red, amber, grey, blue } from '@mui/material/colors';

// Local
import { opportunitySellAndCost } from '../../utils/opportunityFinancials.js';
import currencyToText from '../../CurrencyToText.js';

function calcPeriodMetrics(opps, exchangeRates, organization, cur) {
    const sent = opps.filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft');
    const won = opps.filter(o => o.status === 'completed' || o.status === 'endcustomer-accepted');
    const open = opps.filter(o => o.status === 'sent-to-endcustomer' || o.status === 'opened-by-customer');
    const winRate = sent.length > 0 ? (won.length / sent.length) * 100 : 0;

    let totalRevenue = 0, totalCosts = 0;
    // The quote's own Sell and Cost, in the organization's currency (opportunitySellAndCost).
    for (const o of won) {
        const fin = opportunitySellAndCost({ opportunity: o, exchangeRates, organization });
        if (!fin) continue;
        if (isFinite(fin.sell)) totalRevenue += fin.sell;
        if (isFinite(fin.cost)) totalCosts += fin.cost;
    }

    const gp = totalRevenue - totalCosts;
    const margin = totalRevenue > 0 ? (gp / totalRevenue) * 100 : 0;

    return { totalRevenue, totalCosts, gp, margin, winRate, won: won.length, open: open.length, sent: sent.length, total: opps.length };
}

function getPeriodBuckets(period) {
    const now = dayjs();
    const unit = period === 'month' ? 'month' : period === 'quarter' ? 'quarter' : period === 'year' ? 'year' : 'month';
    const count = period === 'all' ? 6 : 6;
    const buckets = [];
    for (let i = count - 1; i >= 0; i--) {
        const start = now.subtract(i, unit).startOf(unit);
        const end = now.subtract(i, unit).endOf(unit);
        const label = unit === 'month' ? start.format('MMM') : unit === 'quarter' ? `Q${Math.ceil((start.month() + 1) / 3)}` : start.format('YYYY');
        buckets.push({ start: start.valueOf(), end: end.valueOf(), label, isCurrent: i === 0, isPrev: i === 1 });
    }
    return buckets;
}

function deltaIndicator(current, previous) {
    if (!previous || previous === 0) return null;
    const pct = ((current - previous) / Math.abs(previous)) * 100;
    if (Math.abs(pct) < 0.5) return null;
    const isUp = pct > 0;
    return { pct: Math.round(pct), isUp };
}

function AccountAnalytics({ opportunities, exchangeRates, organization, period: externalPeriod, onPeriodChange, updates, log }) {
    const [internalPeriod, setInternalPeriod] = useState('all');
    const period = externalPeriod || internalPeriod;
    const setPeriod = onPeriodChange || setInternalPeriod;
    const cur = organization?.defaults?.currency || 'USD';
    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const allOpps = useMemo(() => (opportunities || []).filter(o => o.status !== 'deleted'), [opportunities]);

    const buckets = useMemo(() => getPeriodBuckets(period), [period]);

    const bucketMetrics = useMemo(() => {
        return buckets.map(b => {
            const opps = allOpps.filter(o => o.created >= b.start && o.created <= b.end);
            return { ...b, metrics: calcPeriodMetrics(opps, exchangeRates, organization, cur) };
        });
    }, [buckets, allOpps, exchangeRates, organization, cur]);

    const currentBucket = bucketMetrics.find(b => b.isCurrent);
    const prevBucket = bucketMetrics.find(b => b.isPrev);
    const current = currentBucket?.metrics || {};
    const prev = prevBucket?.metrics || {};

    // For "all" — use total across all time
    const allTimeMetrics = useMemo(() => calcPeriodMetrics(allOpps, exchangeRates, organization, cur), [allOpps, exchangeRates, organization, cur]);
    const displayMetrics = period === 'all' ? allTimeMetrics : current;

    const kpis = [
        { label: 'Revenue', value: currencyToText(cur, true, fraction.format(displayMetrics.totalRevenue)), raw: displayMetrics.totalRevenue, prevRaw: prev.totalRevenue, color: 'text.primary', goodUp: true, tip: 'Total selling value from quotes won or accepted by the customer' },
        { label: 'GP', value: currencyToText(cur, true, fraction.format(displayMetrics.gp)), raw: displayMetrics.gp, prevRaw: prev.gp, color: displayMetrics.gp >= 0 ? green[700] : red[500], goodUp: true, tip: 'Gross Profit — revenue minus buying costs from won quotes' },
        { label: 'Margin', value: `${fraction.format(displayMetrics.margin)}%`, raw: displayMetrics.margin, prevRaw: prev.margin, color: displayMetrics.gp >= 0 ? green[700] : red[500], goodUp: true, tip: 'Profit margin — GP as a percentage of revenue' },
        { label: 'Win Rate', value: `${fraction.format(displayMetrics.winRate)}%`, raw: displayMetrics.winRate, prevRaw: prev.winRate, color: displayMetrics.winRate >= 50 ? green[700] : displayMetrics.winRate >= 25 ? amber[800] : red[500], goodUp: true, tip: 'Won quotes (accepted by customer or marked as won) out of all sent quotes — includes pending quotes in the denominator' },
        { label: 'Won', value: displayMetrics.won, raw: displayMetrics.won, prevRaw: prev.won, color: 'text.primary', goodUp: true, tip: 'Quotes accepted by the customer or marked as won' },
        { label: 'Open', value: displayMetrics.open, raw: displayMetrics.open, prevRaw: prev.open, color: displayMetrics.open > 0 ? amber[800] : 'text.secondary', goodUp: null, tip: 'Quotes sent to the customer but still awaiting a response' },
    ];

    // Period label
    const periodLabel = (() => {
        const now = dayjs();
        if (period === 'month') return now.format('MMMM YYYY');
        if (period === 'quarter') return `Q${Math.ceil((now.month() + 1) / 3)} ${now.format('YYYY')}`;
        if (period === 'year') return now.format('YYYY');
        return 'All Time';
    })();

    const prevPeriodLabel = (() => {
        const now = dayjs();
        if (period === 'month') return now.subtract(1, 'month').format('MMM YYYY');
        if (period === 'quarter') { const p = now.subtract(1, 'quarter'); return `Q${Math.ceil((p.month() + 1) / 3)} ${p.format('YYYY')}`; }
        if (period === 'year') return now.subtract(1, 'year').format('YYYY');
        return null;
    })();

    const maxRevenue = Math.max(...bucketMetrics.map(b => b.metrics.totalRevenue), 1);

    // Touchpoints for the chart
    const touchpoints = useMemo(() => {
        const points = [];
        const nonCwUpdates = (updates || []).filter(u => !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected');
        nonCwUpdates.forEach(u => {
            if (u.created) points.push({ ts: u.created, type: 'update', label: 'Timeline update' });
        });
        (allOpps || []).filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft').forEach(o => {
            if (o.created) points.push({ ts: o.created, type: 'quote', label: `${o.type === 'contract' ? 'Contract' : 'Quote'} sent` });
            // Status changes from opportunity log
            for (const entry of o.log || []) {
                if (entry.timestamp && entry.type === 'status') {
                    points.push({ ts: entry.timestamp, type: 'status', label: `Status: ${entry.new || 'changed'}` });
                } else if (entry.timestamp && entry.type === 'pipeline') {
                    points.push({ ts: entry.timestamp, type: 'pipeline', label: `Pipeline: ${(entry.new || '').replace('Pipeline: ', '')}` });
                }
            }
        });
        // Account log entries
        (log || []).forEach(entry => {
            if (entry.timestamp && entry.type === 'pipeline') {
                points.push({ ts: entry.timestamp, type: 'pipeline', label: `Pipeline: ${(entry.new || '').replace('Pipeline: ', '')}` });
            } else if (entry.timestamp && entry.type === 'owner') {
                points.push({ ts: entry.timestamp, type: 'owner', label: `Owner changed to ${(entry.new || '').split('@')[0]}` });
            }
        });
        return _.sortBy(points, 'ts');
    }, [updates, allOpps, log]);

    // Map touchpoints to chart x positions
    const chartTimeStart = bucketMetrics[0]?.start;
    const chartTimeEnd = bucketMetrics[bucketMetrics.length - 1]?.end;
    const chartTimeRange = (chartTimeEnd - chartTimeStart) || 1;
    const svgWidth = bucketMetrics.length * 100;

    const touchpointColors = { update: '#8b5cf6', quote: '#f59e0b', status: '#10b981', pipeline: '#6366f1', owner: '#ec4899' };

    // Days since last contact (excluding CW)
    const lastContactDate = useMemo(() => {
        const sentOpps = (allOpps || []).filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft');
        const timestamps = _.compact([
            ...((updates || []).filter(u => !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected').map(u => u.created)),
            ...sentOpps.map(o => o.created),
            ...sentOpps.flatMap(o => (o.log || []).filter(e => e.timestamp && (e.type === 'status' || e.type === 'pipeline')).map(e => e.timestamp)),
        ]);
        return timestamps.length > 0 ? _.max(timestamps) : null;
    }, [updates, allOpps]);

    const daysSinceContact = lastContactDate ? dayjs().startOf('day').diff(dayjs(lastContactDate).startOf('day'), 'day') : null;
    const urgencyColor = daysSinceContact === null ? grey[400] : daysSinceContact <= 7 ? green[600] : daysSinceContact <= 14 ? amber[700] : daysSinceContact <= 30 ? '#f97316' : red[600];
    const urgencyBg = daysSinceContact === null ? grey[50] : daysSinceContact <= 7 ? green[50] : daysSinceContact <= 14 ? amber[50] : daysSinceContact <= 30 ? '#fff7ed' : red[50];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Performance
                </Typography>
                <ToggleButtonGroup
                    size='small'
                    value={period}
                    exclusive
                    onChange={(e, v) => { if (v) setPeriod(v); }}
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '10px', padding: '2px 8px' } }}
                >
                    <ToggleButton value='month'>Month</ToggleButton>
                    <ToggleButton value='quarter'>Quarter</ToggleButton>
                    <ToggleButton value='year'>Year</ToggleButton>
                    <ToggleButton value='all'>All</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Urgency banner */}
            <Box sx={{ background: urgencyBg, border: `1px solid ${urgencyColor}30`, borderRadius: '8px', px: 2, py: 1.5, mb: 2 }}>
                <Typography sx={{ fontSize: '20px', fontWeight: 800, color: urgencyColor, lineHeight: 1.2 }}>
                    {daysSinceContact === null ? 'No contact yet'
                        : daysSinceContact === 0 ? 'You\'re on it! Contacted today'
                        : daysSinceContact === 1 ? 'Great momentum — contacted yesterday'
                        : daysSinceContact <= 3 ? `Strong follow-up — ${daysSinceContact} days ago`
                        : daysSinceContact <= 7 ? `${daysSinceContact} days since last contact — keep it up`
                        : daysSinceContact <= 14 ? `${daysSinceContact} days since last contact`
                        : daysSinceContact <= 30 ? `${daysSinceContact} days — this account needs attention`
                        : `${daysSinceContact} days — this account is going cold`}
                </Typography>
                {lastContactDate && (
                    <Typography sx={{ fontSize: '11px', color: urgencyColor, opacity: 0.7, mt: 0.3 }}>
                        {daysSinceContact <= 7 ? `Last touchpoint: ${dayjs(lastContactDate).format('MMM D, YYYY')}`
                            : daysSinceContact <= 14 ? `Last touchpoint: ${dayjs(lastContactDate).format('MMM D, YYYY')} — consider reaching out`
                            : `Last touchpoint: ${dayjs(lastContactDate).format('MMM D, YYYY')} — reach out today`}
                    </Typography>
                )}
            </Box>

            {/* Period label */}
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#334155', mb: 1.5 }}>
                {periodLabel}
                {prevPeriodLabel && <Typography component='span' sx={{ fontSize: '10px', color: '#94a3b8', ml: 1 }}>vs {prevPeriodLabel}</Typography>}
            </Typography>

            {/* KPIs with delta */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                {kpis.map(kpi => {
                    const delta = period !== 'all' ? deltaIndicator(kpi.raw, kpi.prevRaw) : null;
                    return (
                        <Tooltip key={kpi.label} title={kpi.tip} arrow placement='top'>
                            <Box sx={{ flex: 1, minWidth: '80px', cursor: 'help' }}>
                                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>{kpi.label}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
                                    {delta && (
                                        <Tooltip title={`${delta.isUp ? '+' : ''}${delta.pct}% vs ${prevPeriodLabel}`} arrow placement='top'>
                                            <Typography sx={{
                                                fontSize: '10px', fontWeight: 700,
                                                color: kpi.goodUp === null ? grey[500] : (delta.isUp === kpi.goodUp) ? green[600] : red[500],
                                            }}>
                                                {delta.isUp ? '▲' : '▼'} {Math.abs(delta.pct)}%
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>

            {/* Revenue trend line chart with touchpoints */}
            <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>Revenue Trend & Touchpoints</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {[
                            { type: 'update', label: 'Update' },
                            { type: 'quote', label: 'Quote' },
                            { type: 'status', label: 'Status' },
                            { type: 'pipeline', label: 'Pipeline' },
                            { type: 'owner', label: 'Owner' },
                        ].filter(l => touchpoints.some(t => t.type === l.type && t.ts >= chartTimeStart && t.ts <= chartTimeEnd)).map(l => (
                            <Box key={l.type} sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: touchpointColors[l.type] }} />
                                <Typography sx={{ fontSize: '8px', color: '#94a3b8' }}>{l.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box sx={{ position: 'relative', height: '100px', border: `1px solid ${grey[200]}`, borderRadius: '6px', overflow: 'hidden', background: grey[50] }}>
                    {/* Horizontal grid lines */}
                    {[0.25, 0.5, 0.75].map(pct => (
                        <Box key={pct} sx={{ position: 'absolute', left: 0, right: 0, top: `${(1 - pct) * 80}%`, borderTop: `1px dashed ${grey[200]}` }} />
                    ))}
                    {/* SVG */}
                    <svg width='100%' height='100%' viewBox={`0 0 ${svgWidth} 100`} preserveAspectRatio='none' style={{ position: 'absolute', top: 0, left: 0 }}>
                        {/* Area fill */}
                        <path
                            d={(() => {
                                const points = bucketMetrics.map((b, i) => {
                                    const x = (i / (bucketMetrics.length - 1 || 1)) * (svgWidth - 20) + 10;
                                    const y = maxRevenue > 0 ? 60 - (b.metrics.totalRevenue / maxRevenue) * 50 : 60;
                                    return `${x},${y}`;
                                });
                                const first = points[0]?.split(',')[0] || 10;
                                const last = points[points.length - 1]?.split(',')[0] || 10;
                                return `M${points.join(' L')} L${last},62 L${first},62 Z`;
                            })()}
                            fill='rgba(59,130,246,0.08)'
                        />
                        {/* Revenue line */}
                        <polyline
                            points={bucketMetrics.map((b, i) => {
                                const x = (i / (bucketMetrics.length - 1 || 1)) * (svgWidth - 20) + 10;
                                const y = maxRevenue > 0 ? 60 - (b.metrics.totalRevenue / maxRevenue) * 50 : 60;
                                return `${x},${y}`;
                            }).join(' ')}
                            fill='none'
                            stroke='#3b82f6'
                            strokeWidth='2.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                        {/* Revenue dots */}
                        {bucketMetrics.map((b, i) => {
                            const x = (i / (bucketMetrics.length - 1 || 1)) * (svgWidth - 20) + 10;
                            const y = maxRevenue > 0 ? 60 - (b.metrics.totalRevenue / maxRevenue) * 50 : 60;
                            return (
                                <circle
                                    key={i}
                                    cx={x} cy={y} r={b.isCurrent ? 5 : 3}
                                    fill={b.isCurrent ? '#3b82f6' : '#93c5fd'}
                                    stroke='#fff' strokeWidth='1.5'
                                />
                            );
                        })}
                        {/* Touchpoint markers — bottom strip */}
                        {touchpoints.filter(t => t.ts >= chartTimeStart && t.ts <= chartTimeEnd).map((t, i) => {
                            const x = ((t.ts - chartTimeStart) / chartTimeRange) * (svgWidth - 20) + 10;
                            const color = touchpointColors[t.type] || '#94a3b8';
                            return (
                                <line key={`tp-${i}`} x1={x} y1={68} x2={x} y2={95} stroke={color} strokeWidth='1.5' opacity='0.5' />
                            );
                        })}
                        {touchpoints.filter(t => t.ts >= chartTimeStart && t.ts <= chartTimeEnd).map((t, i) => {
                            const x = ((t.ts - chartTimeStart) / chartTimeRange) * (svgWidth - 20) + 10;
                            const color = touchpointColors[t.type] || '#94a3b8';
                            return (
                                <circle key={`tpd-${i}`} cx={x} cy={68} r='4' fill={color} stroke='#fff' strokeWidth='1.5' />
                            );
                        })}
                    </svg>
                    {/* Touchpoint hover zones */}
                    {touchpoints.filter(t => t.ts >= chartTimeStart && t.ts <= chartTimeEnd).map((t, i) => {
                        const pct = ((t.ts - chartTimeStart) / chartTimeRange) * 100;
                        return (
                            <Tooltip key={`tpt-${i}`} arrow placement='bottom' title={
                                <Box sx={{ fontSize: '10px' }}>
                                    <b>{t.label}</b><br />
                                    {dayjs(t.ts).format('MMM D, YYYY h:mm A')}
                                </Box>
                            }>
                                <Box sx={{
                                    position: 'absolute', bottom: 0, height: '35px', width: '20px',
                                    left: `calc(${Math.min(98, Math.max(1, pct))}% - 10px)`,
                                    cursor: 'pointer', zIndex: 3,
                                }} />
                            </Tooltip>
                        );
                    })}
                    {/* Revenue hover zones */}
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '65px', display: 'flex' }}>
                        {bucketMetrics.map((b, i) => (
                            <Tooltip key={i} arrow placement='top' title={
                                <Box sx={{ fontSize: '11px' }}>
                                    <b>{b.label}{b.isCurrent ? ' (current)' : ''}</b><br />
                                    Revenue: {currencyToText(cur, true, fraction.format(b.metrics.totalRevenue))}<br />
                                    GP: {currencyToText(cur, true, fraction.format(b.metrics.gp))}<br />
                                    Won: {b.metrics.won} | Sent: {b.metrics.sent}
                                </Box>
                            }>
                                <Box sx={{ flex: 1, cursor: 'pointer' }} />
                            </Tooltip>
                        ))}
                    </Box>
                </Box>
                {/* X-axis labels */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3, px: '4px' }}>
                    {bucketMetrics.map((b, i) => (
                        <Typography key={i} sx={{
                            fontSize: '9px', fontWeight: b.isCurrent ? 700 : 400,
                            color: b.isCurrent ? '#334155' : '#94a3b8',
                            flex: 1, textAlign: 'center',
                        }}>
                            {b.label}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export default AccountAnalytics;
